import React from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cx } from './cx';
import IconButton from './IconButton';

export type ToastTone = 'success' | 'warning' | 'danger' | 'info';

export interface ToastProps {
  tone: ToastTone;
  title?: React.ReactNode;
  message: React.ReactNode;
  actions?: React.ReactNode;
  onDismiss: () => void;
  /** Accessible name of the dismiss button (translated). */
  dismissLabel: string;
  className?: string;
}

const META: Record<ToastTone, { icon: React.ReactNode; edge: string; ink: string }> = {
  success: { icon: <CheckCircle2 />, edge: 'border-l-success', ink: 'text-success' },
  warning: { icon: <AlertTriangle />, edge: 'border-l-warning', ink: 'text-warning' },
  danger: { icon: <XCircle />, edge: 'border-l-danger', ink: 'text-danger' },
  info: { icon: <Info />, edge: 'border-l-info', ink: 'text-info' },
};

/** One toast: raised surface, colour only on the edge and the icon. */
export default function Toast({ tone, title, message, actions, onDismiss, dismissLabel, className }: ToastProps) {
  const m = META[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx(
        'grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-start gap-3 rounded-control border border-l-[0.1875rem] k-panel bg-raised px-3.5 py-3 font-ui text-small text-primary',
        m.edge,
        className
      )}
    >
      {/* The icon and the first text line share the dismiss button's 32px band (h-8; pt-1.5 = (32 - 20) / 2),
          so a one-line toast is centred and a longer one keeps the icon on its first line. */}
      <span aria-hidden className={cx('flex h-8 items-center [&>svg]:size-4.5', m.ink)}>
        {m.icon}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5 pt-1.5">
        {title ? <p className="m-0 font-semibold">{title}</p> : null}
        <div className="text-secondary">{message}</div>
        {actions ? <div className="mt-2 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      <IconButton label={dismissLabel} icon={<X />} size="sm" variant="ghost" onClick={onDismiss} />
    </div>
  );
}
